import type { AnalyticsClient } from '@/scopes/common/domain/types'
import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import type { FilterSelection, FiltersInPricesValues, PricesFilterType } from '@/scopes/prices/domain/filters/types'
import type { RoomInfo, RoomSelectorResponse } from '@/scopes/prices/domain/rooms/types'
import type { InitPricesParams } from '@/scopes/prices/domain/types'
import type { IsAccoAvailableApiClient } from '@/scopes/prices/infrastructure/availability/availabilityApiClient'
import type { FiltersApiClient } from '@/scopes/prices/infrastructure/filters/filtersAPIClient'
import type { ParsedInitialState, StateSelection } from '@/scopes/prices/infrastructure/parser/pricesFilterParserWithInitState'
import type { PricesApiClient } from '@/scopes/prices/infrastructure/pricesByDuration/pricesAPIClient'
import type { PromotedPriceApiClient } from '@/scopes/prices/infrastructure/promoted-price/promotedPriceApiClient'
import type { PricesQueryParameterToStateMapper } from '@/scopes/prices/infrastructure/queryParametersToStateMapper/pricesQueryParametersToStateMapper'
import type { RoomSelectorApiClient } from '@/scopes/prices/infrastructure/rooms/roomSelectorApiClient'
import type { PricesStoreApi } from '@/scopes/prices/infrastructure/store'
import type { BusSelectorApiClient } from '@/scopes/prices/infrastructure/transport/bus/busSelectorApiClient'
import type { FlightSelectorApiClient } from '@/scopes/prices/infrastructure/transport/flight/flightSelectorApiClient'
import type { SelfDriveSelectorApiClient } from '@/scopes/prices/infrastructure/transport/selfdrive/selfDriveSelectorApiClient'
import type { TransportSelectorContext, TransportSelectorFilters } from '@/scopes/prices/infrastructure/transport/types'
import { add, format, lastDayOfMonth, max, sub } from 'date-fns'
import {
  isAccommodationAvailableSelector,
  isFirstMonthSelector,
  transportTypeFilterOptionsSelector,
} from '@/scopes/prices/components/selectors'
import {
  createAdditionalDetailsEvent,
  createBusSelectedEvent,
  createFlightSelectedEvent,
  createParticipantsAllocatedEvent,
  createPricesViewedEvent,
} from '@/scopes/prices/domain/analytics'
import { BusType, FlightType } from '@/scopes/prices/domain/transports/types'
import {
  emptyBusResponse,
  emptyPricesResponse,
  emptyRoomsResponse,
  emptySelfDriveResponse,
  emptyTransportResponse,
} from '@/scopes/prices/infrastructure/store'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'

interface PricesActionsDependencies {
  filtersApiClient: FiltersApiClient
  pricesApiClient: PricesApiClient
  roomSelectorApiClient: RoomSelectorApiClient
  flightSelectorApiClient: FlightSelectorApiClient
  selfDriveSelectorApiClient: SelfDriveSelectorApiClient
  promotedPriceAPIClient: PromotedPriceApiClient
  isAccoAvailableApiClient: IsAccoAvailableApiClient
  analyticsClient: AnalyticsClient
  store: PricesStoreApi
  queryParametersToStateMapper: PricesQueryParameterToStateMapper
  queryStringSerializer: QueryStringSerializer<ParsedInitialState>
  browserQueryString: BrowserQueryString
  busSelectorApiClient: BusSelectorApiClient
  optimizelySendEvent: (eventName: string) => Promise<void>
}

interface TransportCriteria extends TransportSelectorContext, TransportSelectorFilters {
  filterSelection: FilterSelection
}

export function pricesActions({
  filtersApiClient,
  pricesApiClient,
  roomSelectorApiClient,
  flightSelectorApiClient,
  selfDriveSelectorApiClient,
  promotedPriceAPIClient,
  isAccoAvailableApiClient,
  analyticsClient,
  store,
  queryParametersToStateMapper,
  queryStringSerializer,
  browserQueryString,
  busSelectorApiClient,
  optimizelySendEvent,
}: PricesActionsDependencies) {
  async function initPrices(params: InitPricesParams) {
    await initializePrices(params)
  }

  async function initPriceTable() {
    await loadInitialPriceTable()
  }

  async function resetPrices({ initialFilterSelection }: { initialFilterSelection: FilterSelection }) {
    const { setIn, accoId, contextItemId, bookingGateId, analytics, promotedPriceId } = store.getState()
    setIn('getFilters.error', false)
    setIn('getPrices.error', false)
    setIn('getRooms.error', false)
    setIn('getTransport.error', false)
    setIn('getPromotedPrice.error', false)
    setIn('isAccommodationAvailable.error', false)
    setIn('promotedPriceResponse', undefined)
    setIn('isAccommodationAvailableResponse', undefined)
    setIn('filterOptions', {})
    setIn('filterSelection', initialFilterSelection)
    await clearStateForPrices()
    updateQueryString()

    await initializePrices({
      accoId,
      contextItemId,
      bookingGateId,
      analytics,
      initialFilterSelection,
      promotedPriceId,
    })
  }

  async function selectFilter({ type, selection }: { type: PricesFilterType, selection: FiltersInPricesValues }) {
    const { setIn, filterSelection } = store.getState()
    if (
      (filterSelection[type] === undefined && selection === undefined)
      || JSON.stringify(filterSelection[type] ?? []) === JSON.stringify(selection)
    ) {
      return
    }

    setIn('filterSelection', {
      ...filterSelection,
      [type]: selection,
      ...(type === 'TransportType' && selection !== 'Flight' && { DepartureAirport: [] }),
    })
    await clearStateForPrices()
    updateQueryString()

    if (type === 'Participants') {
      await loadFilters()
    }
    await loadPrices()
  }

  async function selectFilterMonth(month: string, callback: (startDate: Date) => void) {
    const { isFirstMonth } = isFirstMonthSelector(store.getState())
    const selectedMonthDate = new Date(month)
    const from = max([new Date(format(selectedMonthDate, 'yyyy-MM-01')), new Date()])
    const to = format(lastDayOfMonth(selectedMonthDate), 'yyyy-MM-dd')
    const { minDatePrices } = await loadPriceRange({ dateFrom: String(from), dateTo: to, month })
    let startDate = new Date(from)

    if (
      isFirstMonth(format(new Date(month), 'yyyy-MM-01'))
      && minDatePrices
      && new Date(minDatePrices) > from
    ) {
      startDate = new Date(minDatePrices)
    }
    callback(startDate)
  }

  async function selectRange(criteria: { dateFrom: string, dateTo: string, month: string }) {
    return loadPriceRange(criteria)
  }

  async function selectPrice(priceId: string | null, silentMode: boolean = false) {
    await loadSelectedPriceDetails(priceId)
    if (!silentMode) {
      updateQueryString()
    }
  }

  async function selectRoom(roomNumber: string, roomId: string | null, silentMode: boolean = false) {
    await loadSelectedRoomTransport(roomNumber, roomId, silentMode)
    if (!silentMode) {
      updateQueryString()
    }
  }

  async function setOutboundRouteId(routeId: string | null, silentMode: boolean = false) {
    await applyOutboundRouteSelection(routeId, silentMode)
  }

  async function setInboundRouteId(routeId: string | null, silentMode: boolean = false) {
    await applyInboundRouteSelection(routeId, silentMode)
  }

  async function setOutboundBusId(busId: string | null, silentMode: boolean = false) {
    await applyOutboundBusSelection(busId, silentMode)
  }

  async function setInboundBusId(busId: string | null, silentMode: boolean = false) {
    await applyInboundBusSelection(busId, silentMode)
  }

  async function initializePrices({
    accoId,
    bookingGateId,
    contextItemId,
    analytics,
    initialFilterSelection,
    promotedPriceId,
    refreshPromotedPrice,
  }: InitPricesParams) {
    const { setIn } = store.getState()
    const { initialFilters, initialState } = await readInitialSelection(initialFilterSelection)
    setIn('accoId', accoId)
    setIn('contextItemId', contextItemId)
    setIn('bookingGateId', bookingGateId)
    setIn('analytics', analytics)
    setIn('filterSelection', initialFilters)
    setIn('promotedPriceId', promotedPriceId)
    setIn('refreshPromotedPrice', refreshPromotedPrice ?? false)
    setIn('requestedDepartureDateOnLoad', initialFilters.DepartureDate?.[0] ?? null)

    if (!await checkAccommodationAvailability())
      return

    await loadInitialPromotedPrice(initialFilters, initialState)
    if (initialState) {
      await restoreDeeplinkSelection(initialState)
    }
  }

  async function readInitialSelection(initialFilterSelection: FilterSelection) {
    const queryString = browserQueryString.get()
    if (!queryString)
      return { initialFilters: initialFilterSelection }

    const queryParameters = queryStringSerializer.deserialize(queryString)
    const state = queryParametersToStateMapper.toState(queryParameters)
    let initialState = state?.StateSelection

    if (!initialState) {
      const showPriceTable = new URLSearchParams(queryString.toLowerCase()).get('focuspricetable')
      initialState = showPriceTable?.toLowerCase() === 'true' ? { FocusPriceTable: true } : undefined
    }

    return {
      initialFilters: state?.filterSelection || initialFilterSelection,
      initialState,
    }
  }

  async function loadInitialPromotedPrice(initialFilters: FilterSelection, initialState?: StateSelection) {
    const hasInitialDepartureDate = Boolean(initialFilters.DepartureDate?.[0])
    const hasDeeplinkedSelectedPrice = Boolean(initialState?.SelectedPriceId)
    const shouldFocusPromotedPrice = !hasInitialDepartureDate && !hasDeeplinkedSelectedPrice
    const promotedPrice = await loadPromotedPrice({}).catch(() => undefined)

    if (shouldFocusPromotedPrice && promotedPrice?.legacyDepartureDate?.date) {
      store.getState().setIn('filterSelection.DepartureDate', [promotedPrice.legacyDepartureDate.date])
    }
  }

  async function restoreDeeplinkSelection(initialState: StateSelection) {
    const { setIn } = store.getState()
    const { SelectedPriceId: selectedPriceId, Room: rooms } = initialState

    // The table uses this ID to preserve a cleared duration while filters load.
    if (selectedPriceId) {
      setIn('selectedPriceId', selectedPriceId)
    }
    await loadInitialPriceTable()

    if (!selectedPriceId)
      return

    if (!canRestoreSelectedPrice(selectedPriceId)) {
      setIn('selectedPriceId', null)
      return
    }

    await loadSelectedPriceDetails(selectedPriceId)
    if (!rooms || store.getState().getRooms.error)
      return

    await restoreRoomSelection(rooms)
    await restoreFlightSelection(initialState)
    await restoreBusSelection(initialState)
  }

  function canRestoreSelectedPrice(priceId: string): boolean {
    const { pricesResponse, getPrices } = store.getState()
    return !getPrices.error && pricesResponse.prices.some(price => price.id === priceId)
  }

  async function restoreRoomSelection(rooms: string) {
    const { roomsResponse } = store.getState()
    const roomIds = rooms.split(',')

    for (let index = 0; index < roomIds.length; index++) {
      const roomId = roomIds[index]
      if (roomId === 'empty')
        continue

      const isAvailableRoom = roomsResponse.roomGroups[index]?.rooms.some(room => room.id === roomId)
      if (!isAvailableRoom)
        continue

      await loadSelectedRoomTransport(String(index + 1), roomId, true)
    }
  }

  async function restoreFlightSelection({
    SelectedOutboundRouteId: outboundRouteId,
    SelectedInboundRouteId: inboundRouteId,
  }: StateSelection) {
    if (!outboundRouteId)
      return

    const { flightSelectorResponse, getTransport } = store.getState()
    if (getTransport.error || !flightSelectorResponse.outboundRoutes.some(route => route.id === outboundRouteId))
      return

    await applyOutboundRouteSelection(outboundRouteId, true)
    if (inboundRouteId) {
      await applyInboundRouteSelection(inboundRouteId, true)
    }
  }

  async function restoreBusSelection({
    SelectedOutboundBusId: outboundBusId,
    SelectedInboundBusId: inboundBusId,
  }: StateSelection) {
    if (!outboundBusId)
      return

    const { busSelectorResponse, getTransport } = store.getState()
    if (getTransport.error || !busSelectorResponse.outboundBuses.some(bus => bus.id === outboundBusId))
      return

    await applyOutboundBusSelection(outboundBusId, true)
    if (inboundBusId) {
      await applyInboundBusSelection(inboundBusId, true)
    }
  }

  async function loadInitialPriceTable() {
    const { setIn, filterSelection, selectedPriceId } = store.getState()
    const departureDateForInit = filterSelection.DepartureDate
    const shouldPreserveClearedDuration = Boolean(selectedPriceId) && !filterSelection.Duration?.length

    setIn('filterSelection.DateTo', undefined)
    setIn('filterSelection.DateFrom', undefined)
    setIn('pricesResponse', emptyPricesResponse)
    await loadFilters()

    if (shouldPreserveClearedDuration) {
      setIn('filterSelection.Duration', [])
    }
    if (departureDateForInit?.length) {
      setIn('filterSelection.DepartureDate', departureDateForInit)
    }

    try {
      await loadPrices(true)
    }
    finally {
      setIn('filterSelection.DepartureDate', undefined)
    }
  }

  async function loadPriceRange({ dateFrom, dateTo, month }: { dateFrom: string, dateTo: string, month: string }) {
    const { setIn, filterSelection } = store.getState()
    const newDateFrom = max([new Date(dateFrom), new Date()])
    const dateFromClosestMonday = format(sub(newDateFrom, { days: 7 }), 'yyyy-MM-dd')
    const dateToClosestSunday = format(add(new Date(dateTo), { days: 7 }), 'yyyy-MM-dd')

    setIn('filterSelection', {
      ...filterSelection,
      DateFrom: dateFromClosestMonday,
      DateTo: dateToClosestSunday,
      Month: month,
    })
    updateQueryString()
    await clearStateForPrices()
    const pricesResult = await loadPrices()

    return {
      minDatePrices: pricesResult.dates[0],
      maxDatePrices: pricesResult.dates[pricesResult.dates.length - 1],
    }
  }

  async function loadSelectedPriceDetails(priceId: string | null) {
    store.getState().setIn('selectedPriceId', priceId)
    const { isFlightOnly } = isAccommodationAvailableSelector(store.getState())
    if (isFlightOnly) {
      await clearStateForTransport()
      await loadFlights()
    }
    else {
      await clearStateForRooms()
      await loadRooms()
    }
  }

  async function loadSelectedRoomTransport(roomNumber: string, roomId: string | null, silentMode: boolean) {
    const { setIn, roomsResponse, selectedRooms } = store.getState()
    const { selectedValue: transportType } = transportTypeFilterOptionsSelector(store.getState())
    const newSelectedRooms = { ...selectedRooms, [roomNumber]: roomId }
    setIn('selectedRooms', newSelectedRooms)

    const allRoomsSelected = Object.values(newSelectedRooms).filter(value => value !== null).length === roomsResponse.roomGroups?.length
    if (!allRoomsSelected) {
      await clearStateForTransport()
      return
    }

    const selectedRoomInfos = findSelectedRooms(roomsResponse, newSelectedRooms)
    setIn('selectedRoomInfos', selectedRoomInfos)
    if (!silentMode) {
      sendParticipantsAllocatedEvent()
    }

    const selectedRoomIds = selectedRoomInfos.map(room => room.id)
    const selectedRoomContractIds = selectedRoomInfos.map(room => room.contractId)
    if (transportType === 'Flight') {
      await loadFlights(selectedRoomIds, selectedRoomContractIds)
    }
    else if (transportType === 'SelfDrive') {
      await loadSelfDrive(selectedRoomIds, selectedRoomContractIds)
    }
    else if (transportType === 'Bus') {
      await loadBuses(selectedRoomIds, selectedRoomContractIds)
    }
  }

  function findSelectedRooms(
    { roomGroups }: RoomSelectorResponse,
    selectedRooms: Record<string, string | null>,
  ): RoomInfo[] {
    function findRoom([roomNumber, roomId]: [string, string | null]) {
      if (roomId === null)
        return undefined

      return roomGroups
        ?.find(roomGroup => roomGroup.roomNumber === roomNumber)
        ?.rooms
        ?.find(room => room.id === roomId)
    }

    return Object.entries(selectedRooms).map(findRoom).filter((room): room is RoomInfo => room !== undefined)
  }

  async function applyOutboundRouteSelection(routeId: string | null, silentMode: boolean = false) {
    const { setIn, outboundRoutes } = store.getState()
    if (!routeId) {
      setIn('selectedOutboundRouteId', null)
      setIn('selectedInboundRouteId', null)
      setIn('inboundRoutes', [])
      return
    }

    const outboundRoute = outboundRoutes.find(route => route.id === routeId)
    setIn('selectedOutboundRouteId', routeId)
    const relatedInboundRoutes = outboundRoute?.relatedRoutes
    const selectedInboundRouteId = relatedInboundRoutes?.[0]?.id || null
    setIn('selectedInboundRouteId', selectedInboundRouteId)
    setIn('inboundRoutes', relatedInboundRoutes || [])
    if (!silentMode) {
      updateQueryString()
      await sendFlightEvents({ outbound: routeId, inbound: selectedInboundRouteId })
    }
  }

  async function applyInboundRouteSelection(routeId: string | null, silentMode: boolean = false) {
    store.getState().setIn('selectedInboundRouteId', routeId)
    if (!silentMode) {
      updateQueryString()
      await sendFlightEvents({ inbound: routeId })
    }
  }

  async function applyOutboundBusSelection(busId: string | null, silentMode: boolean = false) {
    const { setIn } = store.getState()
    if (!busId) {
      setIn('selectedOutboundBusId', null)
      return
    }

    setIn('selectedOutboundBusId', busId)
    if (!silentMode) {
      updateQueryString()
      await sendBusEvents({ outbound: busId })
    }
  }

  async function applyInboundBusSelection(busId: string | null, silentMode: boolean = false) {
    const { setIn } = store.getState()
    if (!busId) {
      setIn('selectedInboundBusId', null)
      return
    }

    setIn('selectedInboundBusId', busId)
    if (!silentMode) {
      updateQueryString()
      await sendBusEvents({ inbound: busId })
    }
  }

  async function loadFilters() {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection } = store.getState()
    setIn('getFilters.error', false)
    setIn('getFilters.loading', true)
    try {
      const filtersResult = await filtersApiClient.getFilters({ accoId, contextItemId, bookingGateId, filterSelection })
      setIn('getFilters.loading', false)
      setIn('getFilters.isEmpty', filtersResult.isEmpty)
      setIn('filterOptions', filtersResult)
      if (!filtersResult.isEmpty) {
        setIn('filterSelection', filtersResult.filterSelection)
      }
    }
    catch (error) {
      setIn('getFilters.loading', false)
      setIn('getFilters.error', true)
      throw error
    }
  }

  async function loadPrices(firstLoad: boolean = false) {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection, refreshPromotedPrice } = store.getState()
    setIn('getPrices.error', false)
    setIn('getPrices.loading', true)
    try {
      const pricesResult = await pricesApiClient.getPrices({ contextItemId, bookingGateId, accoId, filterSelection })
      setIn('pricesResponse', pricesResult)
      setIn('getPrices.loading', false)
      if (!firstLoad && refreshPromotedPrice) {
        loadPromotedPrice({ silentRefresh: true }) // no await so user is not affected by it loading behind the scenes
      }
      await sendViewPricesEvent()
      return pricesResult
    }
    catch (error) {
      setIn('getPrices.loading', false)
      setIn('getPrices.error', true)
      throw error
    }
  }

  async function loadRooms() {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection, selectedPriceId } = store.getState()
    setIn('getRooms.error', false)
    setIn('getRooms.loading', true)
    try {
      const roomsResult = await roomSelectorApiClient.getRooms({
        contextItemId,
        bookingGateId,
        accoId,
        filterSelection,
        selectedPriceId: selectedPriceId || '',
      })
      setIn('roomsResponse', roomsResult)
      setIn('getRooms.loading', false)
    }
    catch (error) {
      setIn('getRooms.loading', false)
      setIn('getRooms.error', true)
      throw error
    }
  }

  async function loadPromotedPrice({ silentRefresh }: { silentRefresh?: boolean }) {
    const { setIn, accoId, contextItemId, filterSelection, promotedPriceId, isAccommodationAvailableResponse } = store.getState()
    setIn('getPromotedPrice.error', false)
    setIn('getPromotedPrice.loading', true)
    try {
      const transportType = filterSelection?.TransportType || isAccommodationAvailableResponse.transportTypes[0]
      const promotedPriceResult = await promotedPriceAPIClient.getPromotedPrice({
        contextItemId,
        accoId,
        filterSelection: {
          ...filterSelection,
          ...(transportType && { TransportType: transportType }),
        },
        promotedPriceId,
      })
      setIn('promotedPriceResponse', promotedPriceResult)
      setIn('getPromotedPrice.loading', false)
      setIn('getPromotedPrice.success', true)
      sendAdditionalDetailsEvent()
      return promotedPriceResult
    }
    catch (error) {
      setIn('getPromotedPrice.loading', false)
      setIn('getPromotedPrice.error', true)
      if (!silentRefresh) {
        throw error
      }
    }
  }

  async function checkAccommodationAvailability() {
    const { setIn, accoId, contextItemId, bookingGateId } = store.getState()
    setIn('isAccommodationAvailable.error', false)
    setIn('isAccommodationAvailable.loading', true)
    try {
      const availability = await isAccoAvailableApiClient.isAccommodationAvailable({ contextItemId, accoId, bookingGateId })
      setIn('isAccommodationAvailableResponse', availability)
      setIn('isAccommodationAvailable.loading', false)
      setIn('isAccommodationAvailable.success', true)
      return availability.isAccommodationAvailable
    }
    catch (error) {
      setIn('isAccommodationAvailable.loading', false)
      setIn('isAccommodationAvailable.error', true)
      throw error
    }
  }

  function createTransportCriteria(
    roomIds: string[],
    roomContractIds: string[],
  ): TransportCriteria {
    const { accoId, contextItemId, bookingGateId, filterSelection, selectedPriceId } = store.getState()
    return {
      contextItemId,
      bookingGateId,
      accoId,
      filterSelection: {
        ...filterSelection,
        DateFrom: undefined,
        DateTo: undefined,
      },
      selectedPriceId: selectedPriceId || '',
      roomIds,
      roomContractIds,
    }
  }

  async function loadFlights(selectedRoomIds: string[] = [], selectedRoomContractIds: string[] = []) {
    const { setIn, isAccommodationAvailableResponse } = store.getState()
    setIn('getTransport.error', false)
    setIn('getTransport.loading', true)
    try {
      const roomIds = isAccommodationAvailableResponse.isFlightOnly ? ['1XXX'] : selectedRoomIds
      const flightsResult = await flightSelectorApiClient.getFlights(createTransportCriteria(roomIds, selectedRoomContractIds))
      setIn('flightSelectorResponse', flightsResult)
      setIn('outboundRoutes', flightsResult?.outboundRoutes || [])
      const selectedOutboundRouteId = flightsResult?.outboundRoutes[0]?.id || null
      if (selectedOutboundRouteId) {
        await applyOutboundRouteSelection(selectedOutboundRouteId)
      }
      setIn('getTransport.loading', false)
    }
    catch (error) {
      setIn('getTransport.loading', false)
      setIn('getTransport.error', true)
      throw error
    }
  }

  async function loadSelfDrive(selectedRoomIds: string[] = [], selectedRoomContractIds: string[] = []) {
    const { setIn } = store.getState()
    setIn('getTransport.error', false)
    setIn('getTransport.loading', true)
    try {
      const selfDriveResult = await selfDriveSelectorApiClient.getSelfDrive(createTransportCriteria(selectedRoomIds, selectedRoomContractIds))
      setIn('selfDriveResponse', selfDriveResult)
      setIn('getTransport.loading', false)
    }
    catch (error) {
      setIn('getTransport.loading', false)
      setIn('getTransport.error', true)
      throw error
    }
  }

  async function loadBuses(selectedRoomIds: string[] = [], selectedRoomContractIds: string[] = []) {
    const { setIn, isAccommodationAvailableResponse } = store.getState()
    setIn('getTransport.error', false)
    setIn('getTransport.loading', true)
    try {
      const roomIds = isAccommodationAvailableResponse.isFlightOnly ? ['1XXX'] : selectedRoomIds
      const busesResult = await busSelectorApiClient.getBuses(createTransportCriteria(roomIds, selectedRoomContractIds))
      setIn('busSelectorResponse', busesResult)
      const selectedOutboundBusId = busesResult?.outboundBuses[0]?.id || null
      const selectedInboundBusId = busesResult?.inboundBuses[0]?.id || null
      if (selectedOutboundBusId) {
        await applyOutboundBusSelection(selectedOutboundBusId)
      }
      if (selectedInboundBusId) {
        await applyInboundBusSelection(selectedInboundBusId)
      }
      setIn('getTransport.loading', false)
    }
    catch (error) {
      setIn('getTransport.loading', false)
      setIn('getTransport.error', true)
      throw error
    }
  }

  async function clearStateForTransport() {
    const { setIn } = store.getState()
    setIn('flightSelectorResponse', emptyTransportResponse)
    setIn('selfDriveResponse', emptySelfDriveResponse)
    setIn('busSelectorResponse', emptyBusResponse)
    setIn('outboundRoutes', [])
    setIn('inboundRoutes', [])
    setIn('selectedOutboundRouteId', null)
    setIn('selectedInboundRouteId', null)
    setIn('selectedOutboundBusId', null)
    setIn('selectedInboundBusId', null)
  }

  async function clearStateForRooms() {
    const { setIn } = store.getState()
    setIn('roomsResponse', emptyRoomsResponse)
    setIn('selectedRooms', {})
    setIn('selectedRoomInfos', [])
    await clearStateForTransport()
  }

  async function clearStateForPrices() {
    const { setIn } = store.getState()
    setIn('selectedPriceId', null)
    setIn('pricesResponse', emptyPricesResponse)
    await clearStateForRooms()
  }

  function updateQueryString() {
    const queryParameters = queryParametersToStateMapper.toParameters(store.getState())
    const queryString = queryStringSerializer.serialize(queryParameters)
    browserQueryString.set(queryString)
  }

  async function sendViewPricesEvent() {
    const { analytics, filterSelection, promotedPriceResponse } = store.getState()
    if (!analytics)
      return

    const { TransportType, Participants, Mealplan, Duration } = filterSelection
    const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(Participants || [])

    optimizelySendEvent('price_table_viewed')
    analyticsClient.sendEvent(
      createPricesViewedEvent({
        transport: TransportType ?? '',
        boardType: Mealplan ?? '',
        durations: Duration?.join(',') ?? '',
        partyComposition,
        departureDate: promotedPriceResponse?.legacyDepartureDate.date ?? '',
        entryPrice: promotedPriceResponse?.price.value ?? 0,
        collection: '',
        roomType: '',
        totalGuests,
        analytics,
      }),
    )
  }

  function sendAdditionalDetailsEvent() {
    const { analytics, promotedPriceResponse, filterSelection } = store.getState()
    if (!analytics)
      return

    const { TransportType, Participants, Mealplan } = filterSelection
    const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(Participants || [])
    analyticsClient.sendEvent(
      createAdditionalDetailsEvent({
        transport: TransportType ?? '',
        boardType: Mealplan ?? '',
        duration: promotedPriceResponse?.duration ? `${promotedPriceResponse.duration}` : '',
        partyComposition,
        totalGuests,
        departureDate: promotedPriceResponse?.legacyDepartureDate ? `${promotedPriceResponse.legacyDepartureDate.date}` : '',
        entryPrice: promotedPriceResponse?.price ? `${promotedPriceResponse.price.value}` : '',
        campaignId: promotedPriceResponse?.campaignId || '',
        analytics,
      }),
    )
  }

  function sendParticipantsAllocatedEvent() {
    const { analytics, pricesResponse, selectedPriceId, filterSelection, selectedRoomInfos } = store.getState()
    if (!analytics)
      return

    const selectedPrice = pricesResponse.prices.find(price => price.id === selectedPriceId)
    const { TransportType, Participants } = filterSelection
    const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(Participants || [])

    analyticsClient.sendEvent(
      createParticipantsAllocatedEvent({
        analytics,
        transportType: TransportType ?? '',
        boardType: selectedPrice?.mealplan ?? '',
        duration: selectedPrice?.duration ? selectedPrice.duration.toString() : '',
        partyComposition,
        totalGuests,
        departureDate: selectedPrice?.departureDate ?? '',
        price: selectedPrice?.averagePrice ?? 0,
        roomType: selectedRoomInfos.map(room => room?.name).join(' - '),
        campaignId: selectedPrice?.bullet?.code ?? '',
      }),
    )
  }

  async function sendFlightEvents({ outbound, inbound }: { outbound?: string | null, inbound?: string | null }) {
    const { inboundRoutes, outboundRoutes } = store.getState()
    const outboundRoute = outboundRoutes.find(route => route.id === outbound) || null
    const inboundRoute = inboundRoutes.find(route => route.id === inbound) || null

    if (outboundRoute) {
      analyticsClient.sendEvent(createFlightSelectedEvent({ route: outboundRoute, type: FlightType.Outbound }))
    }
    if (inboundRoute) {
      analyticsClient.sendEvent(createFlightSelectedEvent({ route: inboundRoute, type: FlightType.Inbound }))
    }
  }

  async function sendBusEvents({ outbound, inbound }: { outbound?: string | null, inbound?: string | null }) {
    const { busSelectorResponse } = store.getState()
    if (!busSelectorResponse)
      return

    if (outbound) {
      const index = busSelectorResponse.outboundBuses.findIndex(bus => bus.id === outbound)
      const outboundBus = index !== -1 ? busSelectorResponse.outboundBuses[index] : undefined
      if (outboundBus) {
        analyticsClient.sendEvent(createBusSelectedEvent({ bus: outboundBus, isDefault: index === 0, type: BusType.Outbound }))
      }
    }
    if (inbound) {
      const index = busSelectorResponse.inboundBuses.findIndex(bus => bus.id === inbound)
      const inboundBus = index !== -1 ? busSelectorResponse.inboundBuses[index] : undefined
      if (inboundBus) {
        analyticsClient.sendEvent(createBusSelectedEvent({ bus: inboundBus, isDefault: index === 0, type: BusType.Inbound }))
      }
    }
  }

  return {
    initPrices,
    initPriceTable,
    selectFilter,
    selectFilterMonth,
    selectRange,
    selectPrice,
    selectRoom,
    setOutboundRouteId,
    setInboundRouteId,
    setOutboundBusId,
    setInboundBusId,
    resetPrices,
  }
}
